import { useMemo, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { AppearingFromRight } from 'src/animations/AppearingFromRight';
import {
  useBreakpoint,
  useClearCodeEditor,
  useCodeEditorActions,
  useEditorState,
  useLeftPanelState,
  useRightSidePanelActions,
  useRightSidePanelState,
  useLoadDefaultEditorContent,
  useClearEditorText,
  useCodeAutoSave,
  useDocumentAutoSave,
} from 'src/hooks';
import { ArtifactType, RightPanelSections } from 'src/types';
import { Editor } from '../Editor';
import { MaskImageEditor } from '../MaskImageEditor';
import { getEditorMarkdown } from '../Editor/utils';

// TODO(olha): check design for Tablet. Add outside click if needed
export const RightSidePanel = () => {
  const { isRightPanelExpanded, rightPanelSections, isRightPanelFullWidth } =
    useRightSidePanelState();
  const { isLeftPanelExpanded } = useLeftPanelState();
  const { resetAndCloseRightSidePanelData } = useRightSidePanelActions();
  const { editorType, codeEditorData } = useEditorState();

  const { clearEditorText } = useClearEditorText();
  const { clearCodeEditorContent } = useClearCodeEditor();
  const { setCodeEditorData } = useCodeEditorActions();
  const { onCodeAutoSave } = useCodeAutoSave();
  const { onDocumentAutoSave } = useDocumentAutoSave();

  const [editor] = useLexicalComposerContext();

  useLoadDefaultEditorContent();

  const {
    isTablet,
    isDesktopLAndUp,
    isLaptopLAndUp,
    isDesktopAndUp,
    isDesktopXLAndUp,
    isUltraWideScreen,
  } = useBreakpoint();
  const ref = useRef<HTMLDivElement>(null);

  const Component = useMemo(() => {
    switch (rightPanelSections) {
      case RightPanelSections.IMAGE:
        return <MaskImageEditor />;
      case RightPanelSections.EDITOR:
        return <Editor />;
      default:
        return <></>;
    }
  }, [rightPanelSections]);

  const panelWidth = useMemo(() => {
    switch (rightPanelSections) {
      case RightPanelSections.IMAGE:
        if (isTablet) {
          return 'calc(100vw - 60px)';
        }
        if (isLaptopLAndUp) {
          return 680;
        }
        if (isDesktopLAndUp) {
          return 976;
        }
        return '100vw';

      case RightPanelSections.EDITOR:
        if (isUltraWideScreen) {
          return isLeftPanelExpanded ? 750 : 930;
        }
        if (isDesktopXLAndUp) {
          return isLeftPanelExpanded ? 540 : 690;
        }
        if (isDesktopLAndUp) {
          return isLeftPanelExpanded ? 460 : 660;
        }
        if (isDesktopAndUp) {
          return isLeftPanelExpanded ? 380 : 600;
        }
        if (isLaptopLAndUp) {
          return isLeftPanelExpanded ? 300 : 520;
        }
        if (isTablet) {
          return 'calc(100vw - 60px)';
        }

        return '100vw';

      // default not used for now
      default:
        if (isTablet) {
          return 'calc(100vw - 60px)';
        }
        if (isLaptopLAndUp) {
          return 680;
        }
        if (isDesktopLAndUp) {
          return 820;
        }
        return '100vw';
    }
  }, [
    rightPanelSections,
    isTablet,
    isLaptopLAndUp,
    isDesktopLAndUp,
    isDesktopXLAndUp,
    isDesktopAndUp,
    isLeftPanelExpanded,
    isUltraWideScreen,
  ]);

  const handleExited = async () => {
    resetAndCloseRightSidePanelData();

    if (editorType === ArtifactType.CODE) {
      // TODO(olha): WIP Editor Check if we combine them
      await onCodeAutoSave(codeEditorData?.content || '');
      clearCodeEditorContent();
      setCodeEditorData(null);
    }
    if (editorType === ArtifactType.TEXT) {
      const markdown = getEditorMarkdown(editor);

      await onDocumentAutoSave(markdown);
      clearEditorText();
    }
  };

  return (
    <AppearingFromRight
      inProp={isRightPanelExpanded}
      nodeRef={ref}
      onExited={handleExited}
      width={panelWidth}
      fullWidth={isRightPanelFullWidth}
    >
      {Component}
    </AppearingFromRight>
  );
};
